import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, Download, Eye, FileDown, KeyRound, Plus, RefreshCw, ScrollText, Search, Trash2, Upload, UserCog, X } from "lucide-react";
import { api, useAuth } from "@/auth/AuthContext";

const tabs = [
  { key: "admins", label: "Akun Admin", masterOnly: true, icon: <UserCog size={15} /> },
  { key: "pts", label: "Akun Perguruan Tinggi", masterOnly: false, icon: <Building2 size={15} /> },
  { key: "audit", label: "Audit Log", masterOnly: false, icon: <ScrollText size={15} /> },
];

const ACTION_LABEL = {
  create: "Buat",
  update: "Perbarui",
  delete: "Hapus",
  password_change: "Ubah Password",
  bulk_upsert: "Unggah Massal",
};
const ACTION_TONE = {
  create: "chip-tone-success",
  update: "chip-tone-info",
  delete: "chip-tone-danger",
  password_change: "chip-tone-warn",
  bulk_upsert: "chip-tone-info",
};

export default function ManajemenAkun() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isMaster = user?.role === "master";
  const [tab, setTab] = useState(isMaster ? "admins" : "pts");
  const [admins, setAdmins] = useState([]);
  const [pts, setPts] = useState([]);
  const [query, setQuery] = useState("");
  const [notice, setNotice] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [openDialog, setOpenDialog] = useState(null); // {kind:'create'|'password', target?}
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditFilter, setAuditFilter] = useState({ action: "", target_type: "" });
  const [auditLoading, setAuditLoading] = useState(false);
  const [bulkUploading, setBulkUploading] = useState(false);
  const uploadInputAdm = useRef(null);
  const uploadInputPt = useRef(null);

  const load = async () => {
    try {
      if (isMaster) {
        const r1 = await api.get("/admins");
        setAdmins(r1.data);
      }
      const r2 = await api.get("/pt-accounts");
      setPts(r2.data);
    } catch (err) {
      setErrorMsg(err.response?.data?.detail || "Gagal memuat data.");
    }
  };
  useEffect(() => { load(); }, [isMaster]);

  const loadAudit = async () => {
    setAuditLoading(true);
    try {
      const params = {};
      if (auditFilter.action) params.action = auditFilter.action;
      if (auditFilter.target_type) params.target_type = auditFilter.target_type;
      const res = await api.get("/audit-logs", { params });
      setAuditLogs(res.data);
    } catch (err) {
      setErrorMsg(err.response?.data?.detail || "Gagal memuat audit log.");
    } finally {
      setAuditLoading(false);
    }
  };
  useEffect(() => { if (tab === "audit") loadAudit(); /* eslint-disable-next-line */ }, [tab, auditFilter.action, auditFilter.target_type]);

  const flash = (msg) => { setNotice(msg); setTimeout(() => setNotice(""), 3500); };
  const showErr = (msg) => { setErrorMsg(msg); setTimeout(() => setErrorMsg(""), 4500); };

  const filteredAdmins = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return admins;
    return admins.filter((a) => `${a.nip} ${a.nama_admin} ${a.username}`.toLowerCase().includes(q));
  }, [admins, query]);

  const filteredPts = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return pts;
    return pts.filter((p) => `${p.kodept} ${p.namapt} ${p.username}`.toLowerCase().includes(q));
  }, [pts, query]);

  const handleDelete = async (kind, item) => {
    const label = kind === "admin" ? `admin ${item.nama_admin}` : `PT ${item.namapt}`;
    if (!window.confirm(`Hapus akun ${label}?`)) return;
    try {
      await api.delete(`/${kind === "admin" ? "admins" : "pt-accounts"}/${item.id}`);
      flash(`Akun ${label} dihapus.`);
      load();
    } catch (err) {
      showErr(err.response?.data?.detail || "Gagal menghapus.");
    }
  };

  const submitDialog = async (form) => {
    try {
      if (openDialog.kind === "create") {
        if (tab === "admins") {
          await api.post("/admins", form);
          flash(`Akun admin ${form.nama_admin} disimpan.`);
        } else {
          await api.post("/pt-accounts", form);
          flash(`Akun PT ${form.namapt} disimpan.`);
        }
      } else if (openDialog.kind === "password") {
        const isAdm = openDialog.target.entity === "admin";
        await api.patch(`/${isAdm ? "admins" : "pt-accounts"}/${openDialog.target.id}/password`, { password: form.password });
        flash("Password diperbarui.");
      }
      setOpenDialog(null); load();
    } catch (err) {
      showErr(err.response?.data?.detail || "Gagal menyimpan.");
    }
  };

  const bulkUpload = async (kind, file) => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    setBulkUploading(true);
    setErrorMsg("");
    try {
      const url = kind === "admin" ? "/admins/bulk" : "/pt-accounts/bulk";
      const res = await api.post(url, form, { headers: { "Content-Type": "multipart/form-data" } });
      const { created, updated, errors } = res.data;
      flash(`Berhasil: ${created} baru, ${updated} diperbarui${errors?.length ? `, ${errors.length} gagal` : ""}.`);
      if (errors?.length) showErr(errors.slice(0, 5).join(" · "));
      load();
    } catch (err) {
      showErr(err.response?.data?.detail || "Gagal unggah Excel.");
    } finally {
      setBulkUploading(false);
    }
  };

  const downloadExport = async (kind) => {
    try {
      const url = kind === "admin" ? "/admins/export" : "/pt-accounts/export";
      const res = await api.get(url, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = kind === "admin" ? "admins-lldikti7.xlsx" : "pt-accounts-lldikti7.xlsx";
      document.body.appendChild(link); link.click();
      URL.revokeObjectURL(link.href); document.body.removeChild(link);
    } catch (err) {
      showErr(err.response?.data?.detail || "Gagal mengunduh.");
    }
  };

  const downloadTemplate = async (kind) => {
    try {
      const url = kind === "admin" ? "/admins/template" : "/pt-accounts/template";
      const res = await api.get(url, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = kind === "admin" ? "template-admin.xlsx" : "template-pt.xlsx";
      document.body.appendChild(link); link.click();
      URL.revokeObjectURL(link.href); document.body.removeChild(link);
    } catch (err) {
      showErr(err.response?.data?.detail || "Gagal mengunduh template.");
    }
  };

  return (
    <div className="acct-wrap">
      <div className="acct-head">
        <div>
          <div className="eyebrow">MANAJEMEN AKUN</div>
          <h2>Kelola akun admin & perguruan tinggi</h2>
        </div>
      </div>

      <div className="acct-tabs">
        {tabs.filter((t) => !t.masterOnly || isMaster).map((t) => (
          <button key={t.key} className={`tab-button ${tab === t.key ? "active" : ""}`} data-testid={`acct-tab-${t.key}`} onClick={() => setTab(t.key)}>
            {t.icon} {t.label}
            {t.key !== "audit" && <span className="tab-count">{t.key === "admins" ? admins.length : pts.length}</span>}
            {t.key === "audit" && auditLogs.length > 0 && <span className="tab-count">{auditLogs.length}</span>}
          </button>
        ))}
      </div>

      {notice && <div className="info-banner" data-testid="acct-notice">✓ {notice}</div>}
      {errorMsg && <div className="error-banner" data-testid="acct-error">{errorMsg}</div>}

      {tab !== "audit" && (
      <div className="acct-actionrow">
        <div className="search-wrap"><Search size={15} /><input data-testid="acct-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Cari…" /></div>
        <div className="acct-actions">
          <button className="download-button ghost" data-testid={`acct-add-${tab}`} onClick={() => setOpenDialog({ kind: "create" })}>
            <Plus size={15} /> Tambah {tab === "admins" ? "Admin" : "PT"}
          </button>
          <input ref={tab === "admins" ? uploadInputAdm : uploadInputPt} type="file" accept=".xlsx" style={{ display: "none" }} data-testid={`acct-upload-input-${tab}`}
            onChange={(e) => { bulkUpload(tab === "admins" ? "admin" : "pt", e.target.files?.[0]); e.target.value = ""; }} />
          <button className="download-button ghost" data-testid={`acct-template-${tab}`} onClick={() => downloadTemplate(tab === "admins" ? "admin" : "pt")}>
            <FileDown size={15} /> Unduh Template
          </button>
          <button className="download-button ghost" data-testid={`acct-upload-${tab}`} disabled={bulkUploading} onClick={() => (tab === "admins" ? uploadInputAdm : uploadInputPt).current?.click()}>
            {bulkUploading ? <RefreshCw className="spin" size={15} /> : <Upload size={15} />} {bulkUploading ? "Memproses…" : "Unggah Excel"}
          </button>
          <button className="download-button" data-testid={`acct-export-${tab}`} onClick={() => downloadExport(tab === "admins" ? "admin" : "pt")}>
            <Download size={15} /> Unduh Excel
          </button>
        </div>
      </div>
      )}

      {tab === "audit" && (
        <div className="acct-actionrow">
          <div className="acct-actions" style={{ flexWrap: "wrap", gap: 8 }}>
            <select data-testid="audit-filter-action" className="audit-select" value={auditFilter.action} onChange={(e) => setAuditFilter((s) => ({ ...s, action: e.target.value }))}>
              <option value="">Semua Aksi</option>
              <option value="create">Buat</option>
              <option value="update">Perbarui</option>
              <option value="delete">Hapus</option>
              <option value="password_change">Ubah Password</option>
              <option value="bulk_upsert">Unggah Massal</option>
            </select>
            <select data-testid="audit-filter-target" className="audit-select" value={auditFilter.target_type} onChange={(e) => setAuditFilter((s) => ({ ...s, target_type: e.target.value }))}>
              <option value="">Semua Target</option>
              <option value="admin">Admin</option>
              <option value="pt">PT</option>
            </select>
            <button className="download-button ghost" data-testid="audit-refresh" onClick={loadAudit}>
              <RefreshCw size={15} /> Muat Ulang
            </button>
          </div>
        </div>
      )}

      {tab === "admins" && isMaster && (
        <div className="table-wrap">
          <table data-testid="admins-table">
            <thead><tr><th>NIP</th><th>Nama</th><th>Username</th><th>Dibuat</th><th style={{ width: 160 }}>Aksi</th></tr></thead>
            <tbody>
              {filteredAdmins.map((a) => (
                <tr key={a.id} data-testid={`admin-row-${a.id}`}>
                  <td className="mono">{a.nip}</td>
                  <td><strong>{a.nama_admin}</strong></td>
                  <td className="mono">{a.username}</td>
                  <td className="mono">{a.created_at?.slice(0, 10) || "—"}</td>
                  <td>
                    <button className="folder-chip" data-testid={`admin-pw-${a.id}`} onClick={() => setOpenDialog({ kind: "password", target: { entity: "admin", id: a.id, label: a.nama_admin } })}><KeyRound size={12} /> Password</button>
                    {" "}
                    <button className="folder-chip danger" data-testid={`admin-del-${a.id}`} onClick={() => handleDelete("admin", a)}><Trash2 size={12} /> Hapus</button>
                  </td>
                </tr>
              ))}
              {filteredAdmins.length === 0 && <tr><td colSpan={5} className="empty-state">Belum ada akun admin.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "pts" && (
        <div className="table-wrap">
          <table data-testid="pts-table">
            <thead><tr><th>Kode PT</th><th>Nama PT</th><th>Username</th><th>Dibuat</th><th style={{ width: 220 }}>Aksi</th></tr></thead>
            <tbody>
              {filteredPts.map((p) => (
                <tr key={p.id} data-testid={`pt-row-${p.id}`}>
                  <td className="mono">{p.kodept}</td>
                  <td><strong>{p.namapt}</strong></td>
                  <td className="mono">{p.username}</td>
                  <td className="mono">{p.created_at?.slice(0, 10) || "—"}</td>
                  <td>
                    <button className="folder-chip" data-testid={`pt-view-${p.id}`} onClick={() => navigate(`/kipk/pt/${p.id}`)}><Eye size={12} /> Lihat</button>
                    {" "}
                    <button className="folder-chip" data-testid={`pt-pw-${p.id}`} onClick={() => setOpenDialog({ kind: "password", target: { entity: "pt", id: p.id, label: p.namapt } })}><KeyRound size={12} /> Password</button>
                    {" "}
                    <button className="folder-chip danger" data-testid={`pt-del-${p.id}`} onClick={() => handleDelete("pt", p)}><Trash2 size={12} /> Hapus</button>
                  </td>
                </tr>
              ))}
              {filteredPts.length === 0 && <tr><td colSpan={5} className="empty-state">Belum ada akun PT.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "audit" && (
        <div className="table-wrap">
          <table data-testid="audit-table">
            <thead>
              <tr>
                <th style={{ width: 170 }}>Waktu</th>
                <th style={{ width: 130 }}>Aksi</th>
                <th style={{ width: 90 }}>Tipe</th>
                <th>Target</th>
                <th>Aktor</th>
                <th>Detail</th>
              </tr>
            </thead>
            <tbody>
              {auditLogs.map((log) => (
                <tr key={log.audit_id} data-testid={`audit-row-${log.audit_id}`}>
                  <td className="mono">{formatAuditDate(log.timestamp)}</td>
                  <td>
                    <span className={`folder-chip ${ACTION_TONE[log.action] || ""}`} style={{ pointerEvents: "none" }}>
                      {ACTION_LABEL[log.action] || log.action}
                    </span>
                  </td>
                  <td className="mono">{log.target_type === "admin" ? "Admin" : log.target_type === "pt" ? "PT" : log.target_type}</td>
                  <td><strong>{log.target_label || "—"}</strong></td>
                  <td className="mono">
                    {log.actor}
                    <div className="muted-text" style={{ fontSize: 10 }}>{log.actor_role}</div>
                  </td>
                  <td className="mono" style={{ fontSize: 11 }}>{formatAuditMeta(log.meta)}</td>
                </tr>
              ))}
              {!auditLoading && auditLogs.length === 0 && <tr><td colSpan={6} className="empty-state">Belum ada aktivitas tercatat.</td></tr>}
              {auditLoading && <tr><td colSpan={6} className="empty-state">Memuat…</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {openDialog && <AccountDialog dialog={openDialog} tab={tab} onClose={() => setOpenDialog(null)} onSubmit={submitDialog} />}
    </div>
  );
}

function formatAuditDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

function formatAuditMeta(meta) {
  if (!meta || typeof meta !== "object") return "";
  const parts = [];
  if (meta.username) parts.push(`user: ${meta.username}`);
  if (typeof meta.created === "number") parts.push(`baru: ${meta.created}`);
  if (typeof meta.updated === "number") parts.push(`update: ${meta.updated}`);
  if (typeof meta.errors === "number" && meta.errors > 0) parts.push(`gagal: ${meta.errors}`);
  return parts.join(" · ");
}

function AccountDialog({ dialog, tab, onClose, onSubmit }) {
  const isCreate = dialog.kind === "create";
  const isAdmin = tab === "admins";
  const [form, setForm] = useState(isCreate ? (isAdmin ? { nip: "", nama_admin: "", username: "", password: "" } : { kodept: "", namapt: "", username: "", password: "" }) : { password: "" });
  const [showPw, setShowPw] = useState(false);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const submit = () => {
    // trim all string fields to prevent hidden whitespace mismatch on login
    const cleaned = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, typeof v === "string" ? v.trim() : v]));
    onSubmit(cleaned);
  };
  const title = isCreate ? (isAdmin ? "Tambah / Perbarui Admin" : "Tambah / Perbarui Perguruan Tinggi") : `Ubah password: ${dialog.target?.label || ""}`;
  return (
    <div className="drawer-backdrop" data-testid="acct-dialog-backdrop" onClick={onClose}>
      <div className="detail-drawer" data-testid="acct-dialog" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
        <div className="drawer-top"><div><div className="eyebrow">MANAJEMEN AKUN</div><h2>{title}</h2></div>
          <button className="icon-button" data-testid="acct-dialog-close" aria-label="Tutup" onClick={onClose}><X size={18} /></button>
        </div>
        <form className="acct-form" onSubmit={(e) => { e.preventDefault(); submit(); }}>
          {isCreate && isAdmin && (<>
            <label><span>Nomor Induk Pegawai (NIP)</span><input data-testid="dialog-nip" value={form.nip} onChange={(e) => set("nip", e.target.value)} required /></label>
            <label><span>Nama admin</span><input data-testid="dialog-nama" value={form.nama_admin} onChange={(e) => set("nama_admin", e.target.value)} required /></label>
          </>)}
          {isCreate && !isAdmin && (<>
            <label><span>Kode Perguruan Tinggi</span><input data-testid="dialog-kodept" value={form.kodept} onChange={(e) => set("kodept", e.target.value)} required /></label>
            <label><span>Nama Perguruan Tinggi</span><input data-testid="dialog-namapt" value={form.namapt} onChange={(e) => set("namapt", e.target.value)} required /></label>
          </>)}
          {isCreate && (<label><span>Username <em style={{ fontStyle: "normal", color: "var(--muted)", fontWeight: 400 }}>(tidak case-sensitive saat login)</em></span><input data-testid="dialog-username" value={form.username} onChange={(e) => set("username", e.target.value)} required autoComplete="off" /></label>)}
          <label>
            <span>Password{isCreate ? "" : " baru"}</span>
            <div style={{ position: "relative", display: "flex", alignItems: "stretch" }}>
              <input type={showPw ? "text" : "password"} data-testid="dialog-password" value={form.password} onChange={(e) => set("password", e.target.value)} required autoComplete="new-password" minLength={4} style={{ flex: 1, paddingRight: 74 }} />
              <button type="button" data-testid="dialog-show-pw" onClick={() => setShowPw((s) => !s)} style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", background: "transparent", border: 0, color: "var(--blue)", fontSize: 11, cursor: "pointer", fontFamily: "IBM Plex Mono,monospace" }}>
                {showPw ? "Sembunyi" : "Lihat"}
              </button>
            </div>
          </label>
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <button type="submit" className="download-button" data-testid="dialog-submit">Simpan</button>
            <button type="button" className="download-button ghost" onClick={onClose}>Batal</button>
          </div>
          <p className="muted-text" style={{ marginTop: 6, fontSize: 11 }}>
            Password akan otomatis dibersihkan dari spasi awal/akhir.
            {isCreate && isAdmin && " Jika NIP sudah ada, password akan tergantikan otomatis."}
            {isCreate && !isAdmin && " Jika Kode PT sudah ada, password akan tergantikan otomatis."}
          </p>
        </form>
      </div>
    </div>
  );
}
